// ResponsiveAppBar.jsx
import { useState, useCallback, useEffect } from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Container,
  Toolbar,
  IconButton,
  Tooltip,
} from "@mui/material";
import HideOnScroll from "./HideOnScroll";
import SidebarDrawer from "./SidebarDrawer";
import CenterBrand from "./CenterBrand";
import { logout } from "../../../store/authSlice";
import { logoutUser, setAuthToken } from "../../../api";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import HomeIcon from "@mui/icons-material/Home";
import InfoIcon from "@mui/icons-material/Info";
import ScienceIcon from "@mui/icons-material/Science";
import ArticleIcon from "@mui/icons-material/Article";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import CalculateRoundedIcon from "@mui/icons-material/CalculateRounded";
import { getPublicRecipesPath } from "../../../utils/recipeRoutes";

const APPBAR_H = 56; // dense AppBar height

const publicPages = [
  { label: "Hem", to: "/", icon: <HomeIcon fontSize="small" /> },
  { label: "Kalkylator", to: "/calculator", icon: <CalculateRoundedIcon fontSize="small" /> },
  { label: "Om oss", to: "/about", icon: <InfoIcon fontSize="small" /> },
  { label: "Artiklar", to: "/articles", icon: <ArticleIcon fontSize="small" /> },
  {
    label: "Utforska recept",
    to: getPublicRecipesPath(),
    icon: <RestaurantMenuIcon fontSize="small" />,
  },
  {
    label: "Näringsämnen",
    to: "/nutritions",
    icon: <ScienceIcon fontSize="small" />,
  },
];

export default function ResponsiveAppBar({ openAuth }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isHome = pathname === "/";

  const {
    token,
    is_admin: isAdmin,
    username = "",
  } = useSelector(
    (s) => ({
      token: s.auth.token,
      is_admin: s.auth.is_admin,
      username: s.auth.username,
    }),
    shallowEqual
  );

  const isAuthed = Boolean(token);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Transparent när vi är över heron på Home
  const [overHero, setOverHero] = useState(() => {
    if (typeof window === "undefined") return false;
    return isHome && window.scrollY <= APPBAR_H;
  });

  useEffect(() => {
    if (pathname !== "/") {
      setOverHero(false);
      return;
    }

    let rafId;

    const update = () => {
      const el = document.getElementById("hero-boundary");
      if (!el) {
        // Home är lazy – prova igen nästa frame, håll transparent om vi är i topp
        setOverHero(window.scrollY <= APPBAR_H);
        rafId = requestAnimationFrame(update);
        return;
      }
      const top = el.getBoundingClientRect().top;
      setOverHero(top > APPBAR_H); // ✅ över heron så länge sentineln är nedanför AppBar
    };

    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [pathname]);

  const openLogin = useCallback(() => {
    setDrawerOpen(false);
    setTimeout(() => openAuth?.("login"), 0);
  }, [openAuth]);

  const handleLogout = useCallback(async () => {
    try {
      await logoutUser(); // raderar refresh-cookie i backend
    } finally {
      setAuthToken(null); // tar bort Bearer header från axios
      dispatch(logout());
      localStorage.removeItem("token");
      localStorage.removeItem("username");
      localStorage.removeItem("is_admin");
      localStorage.removeItem("expires_at");
      navigate("/");
    }
  }, [dispatch, navigate]);

  const go = useCallback(
    (to) => {
      navigate(to);
      setDrawerOpen(false);
    },
    [navigate]
  );

  return (
    <HideOnScroll>
      <AppBar
        position="fixed"
        elevation={0}
        color="inherit"
        sx={{
          zIndex: (t) => t.zIndex.drawer,
          backgroundColor: overHero
            ? "transparent"
            : (t) =>
                t.palette.surfaces?.navbar
                  ? `${t.palette.surfaces.navbar}E6` // ~90% opaque so blur shows through
                  : "rgba(20,32,30,0.85)",
          color: overHero ? "surfaces.onDark" : "surfaces.onDark",
          borderBottom: "none",
          boxShadow: overHero
            ? "none"
            : "0 4px 18px rgba(0, 0, 0, 0.18)",
          backdropFilter: overHero ? "none" : "blur(14px) saturate(140%)",
          WebkitBackdropFilter: overHero
            ? "none"
            : "blur(14px) saturate(140%)",
          transform: "translateZ(0)",
          transition:
            "background-color .25s ease, color .25s ease, box-shadow .25s ease, backdrop-filter .25s ease",
        }}
        enableColorOnDark
      >
        <Container maxWidth="xxxl">
          <Toolbar
            disableGutters
            variant="dense"
            sx={{ position: "relative", width: "100%" }}
          >
            <IconButton
              edge="start"
              size="medium"
              color="inherit"
              aria-label="öppna meny"
              onClick={() => setDrawerOpen(true)}
              sx={{
                ml: 0,
                p: 1,
                transition: "transform .25s ease",
                "&:hover": {
                  transform: "rotate(90deg)",
                  bgcolor: "transparent",
                },
              }}
            >
              <MenuIcon
                sx={{
                  fontSize: { xs: "1.4rem", lg: "1.9rem" },
                  filter: overHero
                    ? "drop-shadow(0 2px 6px rgba(0,0,0,0.35))"
                    : "none",
                }}
              />
            </IconButton>

            <CenterBrand
              onClick={() => setDrawerOpen(false)}
              light={overHero}
            />

            <Tooltip
              title={isAuthed ? `Inloggad som ${username}` : "Logga in"}
              arrow
            >
              <IconButton
                edge="end"
                size="medium"
                color="inherit"
                onClick={isAuthed ? () => navigate("/dashboard") : openLogin}
                aria-label={isAuthed ? "Mitt konto" : "Logga in"}
                sx={{
                  ml: "auto",
                  p: 1,
                  transition: "transform .2s ease, background-color .2s ease",
                  "&:hover": {
                    transform: "scale(1.08)",
                    bgcolor: overHero
                      ? "rgba(255,255,255,0.12)"
                      : "rgba(255,255,255,0.08)",
                  },
                }}
              >
                {isAuthed ? (
                  <AccountCircleRoundedIcon
                    sx={{
                      fontSize: { xs: "1.5rem", lg: "1.9rem" },
                      filter: overHero
                        ? "drop-shadow(0 2px 6px rgba(0,0,0,0.35))"
                        : "none",
                    }}
                  />
                ) : (
                  <PersonOutlineRoundedIcon
                    sx={{
                      fontSize: { xs: "1.4rem", lg: "1.85rem" },
                      filter: overHero
                        ? "drop-shadow(0 2px 6px rgba(0,0,0,0.35))"
                        : "none",
                    }}
                  />
                )}
              </IconButton>
            </Tooltip>
          </Toolbar>
        </Container>

        <SidebarDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          isAuthed={isAuthed}
          isAdmin={isAdmin}
          username={username}
          publicPages={publicPages}
          onNavigate={go}
          onLogout={handleLogout}
          onOpenLogin={openLogin}
        />
      </AppBar>
    </HideOnScroll>
  );
}
